import { machineName, store, toast } from '../store';

// The network's state from the hub's reports (every 5 s, {"kind":"net"}): this computer ↔ cmx
// (the user's network) and cmx ↔ the machine. The same rules as internal/netq (Go): a leg is
// judged against its own usual round trip, worse only after 5 s, better only after 20 s.

export type NetState = 'good' | 'busy' | 'poor';

export interface NetReport {
  kind: 'net';
  /** cmx ↔ machine round trip; < 0 when the hub's ping failed. */
  machineMs: number;
  /** This computer ↔ cmx, from the hub's QUIC connection (absent over WebSocket). */
  rttMs?: number;
  minRttMs?: number;
  jitterMs?: number;
  /** Share of the hub's packets to this computer lost since the last report. */
  loss?: number;
}

export interface NetSample {
  rttMs: number;
  usualMs: number;
  /** < 0: unknown */
  loss: number;
  /** < 0: unknown */
  machineMs: number;
}

export interface NetInfo {
  state: NetState;
  sample: NetSample;
  jitterMs: number | null;
}

const RANK: Record<NetState, number> = { good: 0, busy: 1, poor: 2 };
const WORSE_AFTER = 5000;
const BETTER_AFTER = 20000;
const TOAST_EVERY = 120000;

export function verdict(s: NetSample): NetState {
  if (s.rttMs > 250 || s.loss >= 0.05) return 'poor';
  if ((s.rttMs > 2 * s.usualMs && s.rttMs > s.usualMs + 40) || s.loss >= 0.02 || s.machineMs > 50) return 'busy';
  return 'good';
}

/** The slow leg is cmx's (cmx ↔ machine), not this computer's network. */
export function cmxSide(s: NetSample): boolean {
  return s.machineMs > 50 && verdict({ ...s, machineMs: -1 }) === 'good';
}

export class Tracker {
  state: NetState = 'good';
  private worseSince: Record<'busy' | 'poor', number | null> = { busy: null, poor: null };
  private betterAt: number | null = null;
  private betterMax: NetState = 'good';

  /** Takes a sample taken at now (ms); true when the state changed. */
  add(now: number, s: NetSample): boolean {
    const v = verdict(s);
    for (const lvl of ['busy', 'poor'] as const) {
      if (RANK[v] < RANK[lvl]) this.worseSince[lvl] = null;
      else if (this.worseSince[lvl] === null) this.worseSince[lvl] = now;
    }
    const prev = this.state;
    for (const lvl of ['poor', 'busy'] as const) {
      const since = this.worseSince[lvl];
      if (RANK[lvl] > RANK[this.state] && since !== null && now - since >= WORSE_AFTER) {
        this.state = lvl;
        break;
      }
    }
    if (RANK[v] >= RANK[this.state]) {
      this.betterAt = null;
    } else {
      if (this.betterAt === null) {
        this.betterAt = now;
        this.betterMax = v;
      }
      if (RANK[v] > RANK[this.betterMax]) this.betterMax = v;
      if (now - this.betterAt >= BETTER_AFTER) {
        this.state = this.betterMax;
        this.betterAt = null;
        if (RANK[v] < RANK[this.state]) {
          this.betterAt = now;
          this.betterMax = v;
        }
      }
    }
    return this.state !== prev;
  }
}

/** The least round trip over the last 10 minutes (WebSocket connections report none). */
class Usual {
  private samples: { at: number; ms: number }[] = [];
  add(now: number, ms: number): number {
    this.samples.push({ at: now, ms });
    while (this.samples.length && now - this.samples[0].at > 600000) this.samples.shift();
    return Math.min(...this.samples.map((s) => s.ms));
  }
}

export const fmtMs = (v: number) => (v < 10 ? `${Math.round(v * 10) / 10} ms` : `${Math.round(v)} ms`);

class Quality {
  private tracker = new Tracker();
  private usual = new Usual();
  private shown: Partial<Record<NetState, number>> = {};
  private warned = false;
  /** Tests: live reports are ignored while simulated ones are fed (window.__cmx.netReport). */
  paused = false;

  onReport(rep: NetReport, now = Date.now(), simulated = false) {
    if (this.paused && !simulated) return;
    const s: NetSample = { rttMs: 0, usualMs: 0, loss: rep.loss ?? -1, machineMs: rep.machineMs };
    const rtt = store.get().rtt;
    if (rep.rttMs !== undefined && rep.minRttMs !== undefined) {
      s.rttMs = rep.rttMs;
      s.usualMs = rep.minRttMs;
    } else if (rtt !== null) {
      s.rttMs = Math.max(0, rtt - Math.max(0, rep.machineMs));
      s.usualMs = this.usual.add(now, s.rttMs);
    } else {
      return;
    }
    const changed = this.tracker.add(now, s);
    store.set({ net: { state: this.tracker.state, sample: s, jitterMs: rep.jitterMs ?? null } });
    if (changed) this.notice(this.tracker.state, s, now);
  }

  /** A toast on a change, at most one per kind every 2 minutes; "back to normal" only after a warning. */
  private notice(st: NetState, s: NetSample, now: number) {
    if (st === 'good') {
      if (!this.warned) return;
      this.warned = false;
      toast({ key: 'net', title: '✓ Network back to normal', body: `This computer ↔ cmx: rtt ${fmtMs(s.rttMs)}.` }, 3000);
      return;
    }
    const last = this.shown[st];
    if (last !== undefined && now - last < TOAST_EVERY) return;
    this.shown[st] = now;
    this.warned = true;
    if (cmxSide(s)) {
      toast({ key: 'net', title: `⚠ Slow link between cmx and ${machineName()}`, body: `${fmtMs(s.machineMs)} (usually about 1 ms). Not this computer’s network.` }, 5000);
      return;
    }
    let what = `rtt ${fmtMs(s.rttMs)} (usually ${fmtMs(s.usualMs)})`;
    if (s.loss >= 0.005) what += `, ${(100 * s.loss).toFixed(1)}% loss`;
    if (st === 'poor') {
      toast({ key: 'net', kind: 'error', title: '✗ This computer’s network is poor', body: `${what}. Typing and the desktop will lag; cmx’s side is fine.` }, 10000);
    } else {
      toast({ key: 'net', title: '⚠ This computer’s network is slow', body: `${what}. Typing and the desktop may lag; cmx’s side is fine.` }, 5000);
    }
  }
}

export const quality = new Quality();
